import React, { useEffect, useState, type ReactNode } from 'react'
import type { IWebTelemetryService, WebTelemetryConfig } from '@ztechapps/common.contract'
import { hasWindow } from '@ztechapps/common.utils'
import { TelemetryContext } from './TelemetryContext'
import { createWebTelemetryService } from '../services/WebTelemetryService'

export type TelemetryProviderProps = {
  /** Telemetry configuration */
  config: WebTelemetryConfig
  /** Child components */
  children: ReactNode
}

/**
 * Provider component for WebTelemetryService
 *
 * Initializes OpenTelemetry on the client side only (SSR safe).
 *
 * @example
 * ```tsx
 * <TelemetryProvider config={telemetryConfig}>
 *   <App />
 * </TelemetryProvider>
 * ```
 */
export function TelemetryProvider({ config, children }: TelemetryProviderProps) {
  const [service, setService] = useState<IWebTelemetryService | null>(null)

  useEffect(() => {
    // Only initialize on client side
    if (hasWindow() && config.enabled) {
      try {
        const telemetryService = createWebTelemetryService(config)
        setService(telemetryService)

        // Cleanup on unmount
        return () => {
          telemetryService.flush()
        }
      } catch (error) {
        console.error('Failed to initialize telemetry:', error)
      }
    }
    // `config` is a module-level constant at every call site (see `telemetryConfig` in
    // apps/*/config/app-settings) — a stable reference, so depending on the whole object (which
    // `createWebTelemetryService` receives in full) is safe and covers fields beyond these three.
  }, [config])

  return <TelemetryContext.Provider value={service}>{children}</TelemetryContext.Provider>
}
